import React from 'react';
import ReactImageMagnify from 'react-image-magnify';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Image from 'next/image';
import { getSelectedCanvasOptions, isCanvasProduct } from 'tools/canvas-functionality';

const useStyles = makeStyles(theme => ({
	rimRoot: {
		height: '100% !important',
		width: '100% !important'
	},
	rimSmallImage: {
		height: '100% !important',
		width: '100% !important'
	},
	imageWrapper: {
		width: '100%',
		'&.canvas': {
			pointerEvents: 'none',
			border: '90px solid #fff',
			borderBottomWidth: 108,
			margin: '0 20px 20px',
    		alignSelf: 'baseline',
			backfaceVisibility: 'hidden',
			'&.frame-black': {
				borderImage: 'url(/images/Frame-Black-Shade.png) 442 442 565 442 round',
				'&.slides': {
					borderWidth: 30,
					borderBottomWidth: 30,
					borderImage: 'url(/images/Frame-Polaroid-Black.png) 162 round',
				}
			},
			'&.frame-white': {
				borderImage: 'url(/images/Frame-White-Shade.png) 442 442 565 442 round',
				'&.slides': {
					borderWidth: 30,
					borderBottomWidth: 30,
					borderImage: 'url(/images/Frame-Polaroid-White.png) 162 round',
					'& > div': {
						boxShadow: 'inset 0 0 0 1px white',
					}
				}
			},
			'&.frameless': {
				border: 'none'
			},
			[theme.breakpoints.down('sm')]: {
				margin: '0 15px',
				borderWidth: 45,
				borderBottomWidth: 54
			}
		},
		'&.lightbox': {
			display: 'flex',
			width: '100%',
			maxHeight: '100%',
			justifyContent: 'center',
			'& img': {
				objectFit: 'contain',
				pointerEvents: 'none',
			}
		},
		[theme.breakpoints.down('xs')]: {
			display: 'flex',
			height: 'calc(var(--app-height-initial) - 165px)',
			'&.canvas': {
				height: 'unset',
			},
			width: '100%',
			'& > div': {
				width: '100%',
				height: '100%',
				'& > img': {
					objectFit: 'cover'
				}
			}
		}
	},
	videoWrapper: {
		'&.lightbox': {
			display: 'flex',
			width: '100%',
			maxHeight: '100%',
			justifyContent: 'center'
		},
		[theme.breakpoints.down('xs')]: {
			display: 'flex',
			width: '100%',
			height: 'calc(var(--app-height-initial) - 165px)',
			'&.canvas': {
				height: 'unset',
			},
			'& $video': {
				objectFit: 'cover'
			}
		},
	},
	video: {
		objectFit: 'contain'
	}
}));

/**
 * An element that determines the proper tag to use for a media node within a
 * [`Carousel`](/apiReference/carousel/Carousel).
 */
function Media({
	product,
	magnifyProps,
	videoProps,
	src,
	alt,
	height,
	width,
	imageType,
	magnify,
	sources,
	type = 'image',
	ImageMagnifyComponent,
	lightboxActive
}) {
	const classes = useStyles();
	const isCanvas = isCanvasProduct(product) && imageType === 'canvas';

	const adjustMagnifyProps = () => {
		const appliedMagnifyProps = { ...(magnifyProps || {}) };
		appliedMagnifyProps.style = {
			...((magnifyProps && magnifyProps.style) || {}),
			display: 'block',
			objectFit: 'contain'
		};
		appliedMagnifyProps.imageStyle = {
			...((magnifyProps && magnifyProps.imageStyle) || {}),
			objectFit: 'contain'
		};
		appliedMagnifyProps.className = clsx(magnifyProps && magnifyProps.className, classes.rimRoot);
		appliedMagnifyProps.imageClassName = clsx(magnifyProps && magnifyProps.imageClassName, classes.rimSmallImage);
		appliedMagnifyProps.enlargedImageStyle = {
			...((magnifyProps && magnifyProps.enlargedImageStyle) || {}),
			height: '100%'
		};
		return appliedMagnifyProps;
	};

	function isSlide() {
		return product?.categoryIds?.indexOf('c850ec87a10a5f74a0f312236d020c01') >= 0;
	}

	if (type === 'video') {
		if (sources && sources.length) {
			return (
				<video alt={alt} {...videoProps} className={lightboxActive ? '' : classes.mobileCrop}>
					{sources.map(source => (
						<source key={source.src} src={source.src} type={source.type} />
					))}
				</video>
			);
		} else {
			return (
				<div className={[classes.videoWrapper, lightboxActive ? 'lightbox' : ''].join(' ')}>
					<video
						src={src}
						alt={alt}
						{...videoProps}
						className={[classes.video, lightboxActive ? '' : classes.mobileCrop].join(' ')}
						style={{ overflow: 'hidden', width: '100%' }}
						playsInline
						autoPlay
						muted
					/>
				</div>
			);
		}
	} else if (magnify) {
		return (
			<ImageMagnifyComponent
				enlargedImagePosition="over"
				{...adjustMagnifyProps()}
				smallImage={{
					src: src,
					alt: alt,
					isFluidWidth: true
				}}
				largeImage={{...magnify, alt}}
			/>
		);
	} else {
		let canvasOptions = getSelectedCanvasOptions(product);

		return (
			<div className={[classes.imageWrapper, isCanvas && !lightboxActive ? 'canvas ' + canvasOptions.join(' ') : '', lightboxActive ? 'lightbox' : '', isSlide() ? 'slides' : ''].join(' ')}>
				<Image
					src={src}
					alt={alt}
					height={height}
					width={width}
					layout={!lightboxActive ? "responsive" : false}
					contain
					priority={true}
					unoptimized
				/>
			</div>
		);
	}
}

Media.defaultProps = {
	ImageComponent: Image,
	ImageMagnifyComponent: ReactImageMagnify,
};

export default Media;
